import { BaseError, type BaseErrorOptions } from '@canvas/error';
import { UnrecoverableNetworkError } from '@canvas/retry';

import { type State } from '../state-machine/network-state-machine';

export class BoardDocumentMissingError extends UnrecoverableNetworkError {
  constructor() {
    super('document does not exist', 'not-found', { name: 'BoardDocumentMissingError' });
  }
}

function getReason(event: CloseEvent | Event | Error) {
  if (typeof event === 'object' && 'code' in event && typeof event.code === 'number') {
    // CloseEvent type
    const code = `code=${event.code}`;
    const reason =
      'reason' in event && typeof event.reason === 'string' && event.reason.length > 0
        ? `reason=${event.reason}`
        : undefined;
    const wasClean =
      'wasClean' in event && typeof event.wasClean === 'boolean'
        ? `wasClean=${event.wasClean}`
        : undefined;

    return [code, reason, wasClean].filter(Boolean).join(', ');
  }
  if (event instanceof Error) {
    // Error type
    return event.message;
  }
  if (typeof event === 'object' && 'type' in event) {
    // Event type
    return `unknown (event type: ${event.type}), event object keys: ${Object.keys(
      event,
    ).join(', ')})`;
  }
  return `unknown (event object type: ${typeof event}, event object keys: ${Object.keys(
    event || {},
  ).join(', ')})`;
}

export class WsSocketError extends BaseError {
  constructor(
    readonly event: CloseEvent | Event | Error,
    options?: BaseErrorOptions,
  ) {
    super(
      { name: 'WsSocketError', ...options },
      `Error connecting to socket. Reason: ${getReason(event)}`,
    );
  }
}

export type ConnectionErrorStatesMetadata = {
  [key: string]: number;
};

export type PersistentConnectionErrorCounts = {
  [key: string]: ConnectionErrorStatesMetadata;
};

export class PersistentConnectionError extends BaseError {
  constructor(
    readonly cause: Error | WsSocketError,
    public errorCounts: PersistentConnectionErrorCounts,
    readonly state?: State,
    options?: BaseErrorOptions,
  ) {
    super(
      { name: 'PersistentConnectionError', ...options },
      `connection attempts persistently failing. State: ${state}, Cause: ${cause.message}`,
    );
  }
}

export function isPersistentConnectionError(
  error: any,
): error is PersistentConnectionError {
  return error.name === 'PersistentConnectionError' && error.message && error.errorCounts;
}

export function isWsSocketErrorWithCode(
  error: any,
): error is WsSocketError & { event: CloseEvent } {
  return (
    error.name === 'WsSocketError' &&
    error.event &&
    'code' in error.event &&
    typeof error.event.code === 'number'
  );
}

export class FatalConnectionError extends BaseError {
  constructor(message: string | Error, options?: BaseErrorOptions) {
    super(
      { name: 'FatalConnectionError', ...options },
      `canvas-relay client tried to connect but failed and can't retry: ${message}`,
    );
  }
}
